import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { site } from "@/lib/site";
import reviewTulloch from "@/assets/review-tulloch.webp";
import reviewEverette from "@/assets/review-everette.png";
import reviewTaitt from "@/assets/review-taitt.png";
import reviewGordon from "@/assets/review-gordon.png";
import reviewGrant from "@/assets/review-grant.png";
import reviewShroyer from "@/assets/review-shroyer.png";
import reviewCollier from "@/assets/review-collier.png";
import reviewHebdon from "@/assets/review-hebdon.png";
import reviewMclaren from "@/assets/review-mclaren.png";
import reviewWalker from "@/assets/review-walker.png";
import { SmartImage } from "@/components/SmartImage";
import { Stagger, StaggerItem } from "@/components/Stagger";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/reviews")({
  head: () => ({
    meta: [
      ...routeMeta("/reviews"),
      { title: "Client Reviews — CCOB And Company PA | Winter Garden CPA" },
      { name: "description", content: "Read what clients say about working with CCOB And Company PA — a trusted Winter Garden CPA firm with three decades of experience." },
      { property: "og:title", content: "Client Reviews — CCOB And Company PA" },
      { property: "og:description", content: "Real reviews from individuals and business owners we've served." },
    ],
    links: canonicalLinks("/reviews"),
  }),
  component: ReviewsPage,
});

function GoogleLogo({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="currentColor" d="M21.6 12.227c0-.709-.064-1.39-.182-2.045H12v3.868h5.382a4.6 4.6 0 0 1-1.995 3.018v2.51h3.227c1.886-1.737 2.986-4.296 2.986-7.35Z" />
      <path fill="currentColor" d="M12 22c2.7 0 4.964-.895 6.618-2.422l-3.227-2.51c-.895.6-2.04.955-3.391.955-2.605 0-4.81-1.76-5.6-4.123H3.064v2.59A9.996 9.996 0 0 0 12 22Z" />
      <path fill="currentColor" d="M6.4 13.9a6.01 6.01 0 0 1 0-3.8V7.51H3.064a9.996 9.996 0 0 0 0 8.98L6.4 13.9Z" />
      <path fill="currentColor" d="M12 5.977c1.468 0 2.786.505 3.823 1.496l2.867-2.867C16.96 2.99 14.696 2 12 2 8.09 2 4.71 4.245 3.064 7.51L6.4 10.1c.79-2.363 2.995-4.123 5.6-4.123Z" />
    </svg>
  );
}

const reviews = [
  {
    img: reviewTulloch as string | null,
    name: "Extraterrestrial Tulloch",
    meta: "Local Guide · 21 reviews · 5 photos",
    age: "a year ago",
    quote: "One of the best accounting firms in the USA don't hessite to call MR Locksley he will get you what you need",
  },
  {
    img: reviewEverette,
    name: "Joseph Everette",
    meta: "Local Guide · 30 reviews · 49 photos",
    age: "a month ago",
    quote: "Kevin has taken care of our taxes for three years in a row. Cannot say enough of his professionalism and flexibility. Thanks again for always coming through during tax season!",
  },
  {
    img: reviewWalker,
    name: "Samantha Walker",
    meta: "3 reviews",
    age: "5 years ago",
    quote: "I found Locksley Cameron CPA on Google while looking for a tax preparer for some assistance with my taxes. Before meeting with Locksley we spoke on the phone and he gave me advice and his expertise on my situation. After meeting with Locksley he made me feel comfortable and was able to help me prepare my taxes for 2019 in a timely manner. I recommend Locksley and will continue to use his services in the future.",
  },
  {
    img: reviewMclaren,
    name: "Barbie 386 McLaren",
    meta: "3 reviews · 3 photos",
    age: "6 years ago",
    quote: "Locksley Cameron is an awesome CPA. I started seeing him in 2018. I have a fear of the IRS seeing I was audited earlier. He was able to alleviate all my fears and answered all my questions. I would definitely recommend him.",
  },
  {
    img: null,
    name: "Kevin Baker",
    meta: "2 reviews",
    age: "6 years ago",
    quote: "Excellent Service. Mr. Cameron's accounting expertise added to the deep care he has for his clients and attention to detail he pays to his work will leave you extremely satisfied. I definitely recommend his services in handling any of your accounting needs.",
  },
  {
    img: reviewHebdon,
    name: "Derk Hebdon",
    meta: "Local Guide · 54 reviews · 49 photos",
    age: "8 years ago",
    quote: "Locksley A Cameron is a great CPA! He is very down to earth and honest. A deeply caring and professional man that knows taxes and accounting. Without question I recommend him for all your individual or business accounting needs.",
  },
  {
    img: reviewCollier,
    name: "Carol Collier",
    meta: "1 review",
    age: "6 years ago",
    quote: "Locksley has taken us through a rigorous review of our 2018 accounts this year and is providing us with guidance on how to record our finances more efficiently. We are looking forward to working with him to improve our financial management and performance in the business.",
  },
  {
    img: reviewShroyer,
    name: "Joshua Shroyer",
    meta: "1 review",
    age: "4 years ago",
    quote: "I had a really great interaction with Mr. Cameron for a consultation. He was very patient and helped to educate me on a couple things as well during our conversation.",
  },
  {
    img: reviewGrant,
    name: "Rai-Leigh Grant",
    meta: "Local Guide · 16 reviews · 2 photos",
    age: "7 years ago",
    quote: "My family has worked with him years and now I use him. He's honest and he cares and he's timely. God Bless Him!!",
  },
  {
    img: reviewGordon,
    name: "Denise Gordon",
    meta: "1 review",
    age: "6 years ago",
    quote: "Just simply put he is one of the best. Would recommend both individuals and businesses to use his services.",
  },
  {
    img: null,
    name: "Belinda Gray",
    meta: "5 reviews · 12 photos",
    age: "5 years ago",
    quote: "Thank you Mr. Cameron A Locksley for your services. You answered all my questions, lead me in the right direction to what I should do next. See you next tax time!",
  },
  {
    img: reviewTaitt,
    name: "Jose Taitt",
    meta: "2 reviews",
    age: "6 years ago",
    quote: "He is very understanding and good at what he do.",
  },
  {
    img: null,
    name: "Richard Harze",
    meta: "Local Guide · 11 reviews",
    age: "6 years ago",
    quote: "It was a pleasure talking to Mr. Cameron.",
  },
];

function ReviewsPage() {
  const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };
  return (
    <>
      <PageHero
        eyebrow="Client Reviews"
        title="What Our Clients Say"
        subtitle="Three decades of relationships, built one return — and one conversation — at a time."
        image={img.heroAbout}
      />
      <section className="py-20">
        <div className="container-x">
          <Stagger className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
            {reviews.map((r, i) => (
              <StaggerItem
                key={r.name + i}
                className="bg-card border border-border rounded-2xl p-7 hover:border-gold transition-colors"
              >
                <div className="flex items-center gap-3">
                  {r.img ? (
                    <SmartImage src={r.img} alt={r.name} className="h-11 w-11 rounded-full object-cover" />
                  ) : (
                    <div className="h-11 w-11 rounded-full bg-primary/10 grid place-items-center text-primary font-display text-lg">
                      {r.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="font-medium text-primary">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.meta}</div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <div className="flex gap-0.5 text-gold">{[...Array(5)].map((_,idx)=><Star key={idx} className="h-4 w-4 fill-current" />)}</div>
                  <span className="text-xs text-muted-foreground">{r.age}</span>
                </div>
                <p className="mt-3 text-foreground/85 leading-relaxed text-sm">{r.quote}</p>
              </StaggerItem>
            ))}
          </Stagger>

          <motion.div {...fadeUp} className="mt-12 flex justify-center">
            <a
              href={site.social.maps}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-foreground text-background font-medium hover:bg-foreground/90 transition-colors shadow-md"
            >
              <GoogleLogo className="h-5 w-5" />
              More reviews on Google
              <ExternalLink className="h-4 w-4 opacity-70" />
            </a>
          </motion.div>

          <motion.div {...fadeUp} className="mt-16 rounded-3xl bg-primary text-primary-foreground p-10 md:p-14 text-center">
            <h2 className="font-display text-3xl md:text-4xl">Worked with us? We'd love to hear it.</h2>
            <p className="mt-3 opacity-90 max-w-xl mx-auto">Your feedback helps other Central Florida individuals and business owners find a CPA they can trust.</p>
            <a href={site.social.maps} target="_blank" rel="noreferrer" className="btn-gold mt-7">
              Leave a Google Review <ExternalLink className="h-4 w-4" />
            </a>
          </motion.div>
        </div>
      </section>
    </>
  );
}
